/**
 * useHasUnseenVersions — "What's New" unseen-badge signal.
 *
 * Encapsulates the single source of truth for the Whats_New_Badge: it compares
 * the Changelog's Latest_Version against the client-side Last_Seen_Version and
 * returns whether an unseen newer version exists. Per KDD-5, this signal is
 * derived from the changelog artifact (`getLatestVersion`) vs `localStorage`
 * (`readLastSeenVersion`) and is deliberately independent of the deployed
 * runtime version string.
 *
 * Behaviour:
 * - Empty changelog (no Latest_Version) → false (no badge).
 * - First visit (no Last_Seen_Version stored) → initialise Last_Seen_Version to
 *   Latest_Version and treat everything as seen → false (Req 6.6). The
 *   initialising write is performed as a side effect in `useEffect`, never
 *   during render.
 * - Returning visit → true only while Latest_Version is greater than
 *   Last_Seen_Version by semantic version ordering (Req 6.2); otherwise false
 *   when Last_Seen_Version is not less than Latest_Version (Req 6.5).
 *
 * This hook does NOT record Latest_Version as seen on a normal returning visit —
 * that recording happens when the user opens the Changelog page. The only write
 * this hook performs is the first-visit initialisation (Req 6.6). Reads are
 * cheap, synchronous, and side-effect-free, so the returned boolean is computed
 * during render; the underlying storage wrapper swallows errors, so this hook
 * never throws.
 *
 * Validates: Requirements 6.1, 6.2, 6.5, 6.6; design KDD-5.
 */

import { useEffect } from 'react';
import { getLatestVersion } from '../services/changelog';
import { compareSemver } from '../services/semver';
import { readLastSeenVersion, writeLastSeenVersion } from '../services/last-seen-version';

/**
 * Return whether an unseen newer changelog version exists (the badge signal).
 *
 * @returns `true` while a version newer than the Last_Seen_Version exists on a
 * returning visit; `false` for an empty changelog, on first visit, and once the
 * Last_Seen_Version is not less than the Latest_Version.
 */
export function useHasUnseenVersions(): boolean {
  const latest = getLatestVersion();
  const lastSeen = readLastSeenVersion();

  // First-visit initialisation (Req 6.6): if there is a changelog but nothing
  // stored yet, mark the current latest as seen. Performed as a side effect so
  // render stays pure; subsequent renders then observe the stored value.
  useEffect(() => {
    if (latest !== null && lastSeen === null) {
      writeLastSeenVersion(latest.version);
    }
  }, [latest, lastSeen]);

  // Empty changelog → no badge (Req 6.1). First visit → treat all as seen and
  // hide the badge on this render too (Req 6.6).
  if (latest === null || lastSeen === null) {
    return false;
  }

  // Returning visit: badge only while an unseen newer version exists (Req 6.2);
  // hidden once last-seen is not less than latest (Req 6.5).
  return compareSemver(latest.version, lastSeen) > 0;
}
